import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  useLocation, useNavigate, useParams, useSearchParams,
} from 'react-router-dom';
import { useTheme } from 'styled-components';
import useGetAlertById from '../../API/hooks/alerts/useGetAlertById';
import useGetDeviceByDeviceId from '../../API/hooks/devices/useGetDeviceByDeviceId';
import useGetDriverById from '../../API/hooks/drivers/useGetDriverById';
import useGetVehicleById from '../../API/hooks/vehicles/useGetVehicleById';
import Icon from '../../components/common/Icon/Icon';
import Modal from '../../components/common/Modal/Modal';
import { IconKeys } from '../../components/common/Icon/IconKeys';
import Tags from '../../components/common/Tags';
import { Button } from '../../components/ui/Button/Button';
import HeaderWrapper from '../../components/ui/DetailsPageHeaderWrapper/HeaderWrapper';
import { alertEventsMap } from '../Alerts/constants/alertEventsMap';
import S from './AlertDetailsStyles';
import MultipleVideo from '../../components/VideoContainer/MultipleVideo/MultipleVideo';
import MapWidget from '../../components/common/Map/Map';
import useGetFleetId from '../../API/hooks/useGetFleetId';
import useNoThumbnail from '../../theme/useNoThumbnail';
import Notes from '../../components/Notes/Notes';
import CreateNote from '../../components/Notes/CreateNote/CreateNote';
import AlertTags from './AlertTags/AlertTags';
import { getAlertThumbnail, getAlertDriverPhotoUrl } from '../Alerts/helpers';
import DetailsPanel from '../../components/DetailsPanel/DetailsPanel';
import DeleteModal from '../../components/common/DeleteModal/DeleteModal';
import useDeleteAlertById from '../../API/hooks/alerts/useDeleteAlertById';
import useGetRole from '../../API/hooks/useGetRole';
import ConfirmModal from '../../components/common/ConfirmModal/ConfirmModal';
import usePatchAlertValidation from '../../API/hooks/alerts/usePatchAlertValidation';
import { ThemeType } from '../../theme/themeType';
import { getAlertDetailsReturnPath, getAlertDetailsReturnState } from '../../utils/alertNavigation';
import { withScrollRestoreState } from '../../utils/scrollRestoration';
import useDetailsListNavigation from '../../components/common/DetailsListNavigation/DetailsListNavigation';
import { entityMatchingId } from '../../utils/entityMatching';

function AlertDetailsPage(): JSX.Element {
  const theme = useTheme() as ThemeType;
  const noThumbnail = useNoThumbnail();
  const fleetId = useGetFleetId();
  const navigate = useNavigate();
  const location = useLocation();
  const [addNoteModalOpen, toggleAddNoteModalOpen] = useState(false);
  const [deleteModalOpen, toggleDeleteModalOpen] = useState(false);
  const [validationModalOpen, toggleValidationModalOpen] = useState(false);
  const [addValidationNoteModalOpen, toggleAddValidationNoteModal] = useState(false);
  const { id: alertId } = useParams();
  const listNavigation = useDetailsListNavigation({
    entity: 'alerts',
    currentId: alertId,
  });
  const [searchParams] = useSearchParams();
  const camera = searchParams.get('camera') || 'outward';
  const { isFleetViewer, isFleetManager } = useGetRole();
  const { data: alert, isLoading, error } = useGetAlertById(alertId as string, fleetId);
  const { data: device } = useGetDeviceByDeviceId(alert?.device?.deviceId as string, fleetId);
  const { data: driver } = useGetDriverById(alert?.driver?.driverId as string, fleetId);
  const { data: vehicle } = useGetVehicleById(alert?.vehicle?.vehicleId as string, fleetId);
  const alertDevice = entityMatchingId(device, device?.deviceId, alert?.device?.deviceId);
  const alertDriver = entityMatchingId(driver, driver?._id, alert?.driver?.driverId);
  const alertVehicle = entityMatchingId(vehicle, vehicle?._id, alert?.vehicle?.vehicleId);
  const { shortDesc, fullDesc, badge } = alertEventsMap[alert?.name as string] || {};

  const { mutate: mutateDelete } = useDeleteAlertById(
    () => {
      toast.success('Alert deleted successfully');
      navigate('/alerts/');
    },
    () => {
      toast.error('Alert failed to delete');
      toggleDeleteModalOpen(false);
    },
  );

  const { mutate: mutateValidate } = usePatchAlertValidation(
    (updatedAlert) => {
      toast.success(`Alert marked as ${updatedAlert.isValid ? 'valid' : 'invalid'}`);
      if (!updatedAlert.isValid) {
        toggleAddValidationNoteModal(true);
      }
    },
    () => toast.error('Alert failed to mark'),
  );

  const handleBackToList = (): void => navigate('/alerts/');
  const thumbnail = getAlertThumbnail(alert) || noThumbnail;
  const driverPhotoUrl = getAlertDriverPhotoUrl(alert);
  const videoIsSynced = alert?.timeSynchronization === 'ALL_SYNCHRONIZED';
  const driverForPanel = useMemo(() => {
    const resolvedDriver = alertDriver ?? alert?.driver ?? null;
    if (!resolvedDriver || !driverPhotoUrl) {
      return resolvedDriver;
    }
    return {
      ...resolvedDriver,
      avatarUrl: driverPhotoUrl,
    };
  }, [alert?.driver, alertDriver, driverPhotoUrl]);
  const returnPath = getAlertDetailsReturnPath(location.state);

  const handleGoBack = (): void => {
    if (listNavigation.backToList) {
      listNavigation.backToList();
      return;
    }
    if (returnPath) {
      navigate(returnPath, {
        state: withScrollRestoreState(getAlertDetailsReturnState(location.state)),
      });
      return;
    }

    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    handleBackToList();
  };

  useEffect(() => {
    if (error) {
      navigate('/alerts/', { replace: true });
    }
  }, [error, navigate]);

  const deleteAlert = (): void => mutateDelete({
    fleetId,
    alertId: alertId || '',
  });

  const validateAlert = (): void => mutateValidate({
    fleetId,
    alertId: alertId || '',
    isValid: !alert?.isValid,
  });

  const handleDelete = (): void => {
    toggleDeleteModalOpen(true);
  };

  const handleAlertValidation = (): void => {
    toggleValidationModalOpen(true);
  };

  const handleAddNote = (): void => {
    toggleAddNoteModalOpen(true);
  };

  if (error) {
    return <div>Loading...</div>;
  }
  return (
    <>
      {isLoading && (<div>Loading...</div>)}

      {!!alert && (
        <S.Wrapper>
          <HeaderWrapper>
            <div className="details-row">
              <div className="overview-container">
                <Button height="44px" padding="10px 12px" onClick={handleGoBack}>
                  <Icon iconKey={IconKeys.ARROW_LEFT} />
                </Button>
                <Tags.ItemOverview
                  avatar={thumbnail}
                  avatarAlt="alert"
                  title={shortDesc}
                  subtitle={fullDesc}
                />
                {listNavigation.controls}
              </div>
              <div className="actions-wrapper">
                <div className="border-line" />
                <Button
                  className="alert-validation"
                  height="44px"
                  padding="10px 12px"
                  title={`Mark alert as ${alert.isValid ? 'invalid' : 'valid'}`}
                  onClick={handleAlertValidation}
                  disabled={!alertId || !alert || isFleetViewer}
                >
                  {`Mark alert as ${alert.isValid ? 'invalid' : 'valid'}`}
                </Button>
                <Button
                  height="44px"
                  padding="10px 12px"
                  title="Delete alert"
                  onClick={handleDelete}
                  disabled={!alertId || !alert || isFleetViewer}
                >
                  <Icon iconKey={IconKeys.DELETE} />
                </Button>
                {/* <Button
                height="44px"
                padding="10px 12px"
                title="Add note"
                onClick={handleAddNote}
              >
                <Icon iconKey={IconKeys.ADD_NOTE} />
              </Button> */}
              </div>
            </div>
            <AlertTags alert={alert} />
          </HeaderWrapper>
          <div className="widgets-container-video">
            <div className="video-container">
              <MultipleVideo
                key={alertId}
                active={camera}
                fleetId={fleetId}
                alertId={alertId}
                deviceId={alert?.deviceId}
                driverPhotoUrl={driverPhotoUrl}
                info={{ speedMs: alert.speedMs }}
                synchronized={videoIsSynced}
                deviceStatus={alertDevice?.isOnline}
              />
              <MapWidget
                fleetId={fleetId}
                alerts={(alert.lon || alert.lat) ? [alert] : []}
                disableAlertMarkerNavigation
              />
            </div>
          </div>
          <DetailsPanel driver={driverForPanel} vehicle={alertVehicle} device={alertDevice} />
          <Notes key={alertId} fleetId={fleetId} entityName="alerts" entityId={alertId} />
        </S.Wrapper>
      )}
      {addNoteModalOpen && alertId && (
        <Modal
          title="Add note for event"
          state={addNoteModalOpen}
          toggleState={toggleAddNoteModalOpen}
        >
          <CreateNote fleetId={fleetId} entityName="alerts" entityId={alertId} toggleModal={toggleAddNoteModalOpen} />
        </Modal>
      )}
      {deleteModalOpen && alertId && (
        <Modal
          title="Delete event"
          state={deleteModalOpen}
          toggleState={toggleDeleteModalOpen}
        >
          <DeleteModal
            title="Are you sure you want to delete this alert? It will affect driver and vehicle assigned to current alert."
            onClose={() => toggleDeleteModalOpen(false)}
            onDelete={deleteAlert}
            btnConfirmText="Delete"
          />
        </Modal>
      )}
      {validationModalOpen && alertId && (
        <Modal
          title={`Mark alert as ${alert?.isValid ? 'invalid' : 'valid'}`}
          state={validationModalOpen}
          toggleState={toggleValidationModalOpen}
        >
          <ConfirmModal
            title={`Are you sure you want to mark this alert as ${alert?.isValid ? 'INVALID' : 'VALID'}? It will affect driver and vehicle assigned to current alert.`}
            onClose={() => toggleValidationModalOpen(false)}
            onConfirm={() => {
              validateAlert();
              toggleValidationModalOpen(false);
            }}
            btnConfirmText={`Mark as ${alert?.isValid ? 'invalid' : 'valid'}`}
            btnConfirmBackgroundColor={alert?.isValid ? theme.colors.red : theme.colors.green}
          />
        </Modal>
      )}
      {addValidationNoteModalOpen && (
        <Modal
          title="Why alert was marked as invalid?"
          state={addValidationNoteModalOpen}
          toggleState={toggleAddValidationNoteModal}
          onClose={() => toggleAddValidationNoteModal(false)}
        >
          <CreateNote
            fleetId={fleetId}
            entityName="alerts"
            title="Alert was marked as invalid"
            editableTitle
            isAutoGenerated
            entityId={alertId || ''}
            toggleModal={toggleAddValidationNoteModal}
            onClose={() => toggleAddValidationNoteModal(false)}
          />
        </Modal>
      )}
    </>
  );
}

export default AlertDetailsPage;
